// Issue with watermelonDB library https://github.com/Nozbe/WatermelonDB/issues/629
// @ts-nocheck
import { Model, Q } from '@nozbe/watermelondb';
import {
    field,
    date,
    readonly,
    relation,
    lazy,
} from '@nozbe/watermelondb/decorators';
import { setGenerator } from '@nozbe/watermelondb/utils/common/randomId';
import { v4 as uuidv4 } from 'uuid';
import { EventCategory, EventModel } from '../../types/Events';
import type { ContactSimple } from './Contact';
import type { HorseSimple } from './Horse';
import { LocationSimple } from './Location';

setGenerator(uuidv4);

export default class Event extends Model {
    static table = 'events';

    static associations = {
        event_horses: { type: 'has_many', foreignKey: 'event_id' },
        event_contacts: { type: 'has_many', foreignKey: 'event_id' },
        event_users: { type: 'has_many', foreignKey: 'event_id' },
        organisations: { type: 'belongs_to', key: 'organisation_id' },
        locations: { type: 'belongs_to', key: 'location_id' },
    };

    @relation('users', 'user_id') user;
    @relation('organisations', 'organisation_id') organisation;
    @relation('locations', 'location_id') location;

    @field('title') title;
    @field('address') address;
    @field('starts_time') startsTime;
    @field('ends_time') endsTime;
    @field('notes') notes;
    @field('user_reminder') userReminder;
    @field('send_reminders_to_contacts') contactsReminder;
    @field('send_reminders_to_owners') ownersReminder;
    @field('send_invites_to_contacts') contactsInvite;
    @field('send_invites_to_contacts_at') sendInvitesToContactsAt;
    @field('location_id') locationId;
    @field('user_id') userId;
    @field('organisation_id') organisationId;
    @field('timezone') timezone;
    @field('category') category;
    @field('is_recurring') isRecurring;
    @field('recurrence_pattern') recurrencePattern;
    @field('recurrence_end_date') recurrenceEndDate;
    @field('original_event_id') originalEventId;

    @date('created_at') createdAt;
    @date('updated_at') updatedAt;

    @lazy
    horses = this.collections
        .get('horses')
        .query(Q.on('event_horses', 'event_id', this.id));

    @lazy
    contacts = this.collections
        .get('contacts')
        .query(Q.on('event_contacts', 'event_id', this.id));

    @lazy
    entries = this.collections
        .get('entries')
        .query(Q.where('event_id', this.id));

    @lazy
    eventContactReminders = this.collections
        .get('event_contact_reminders')
        .query(Q.where('event_id', this.id));

    @lazy
    users = this.collections
        .get('users')
        .query(Q.on('event_users', 'event_id', this.id));
}

export interface EventSimple {
    id: string;
    userId: string;
    organisationId: string;
    locationId: string;
    title: string;
    address: string;
    startsTime: string;
    endsTime: string;
    notes: string;
    userReminder: string;
    sendRemindersToContacts: bool;
    sendRemindersToOwners: bool;
    sendInvitesToContacts: bool;
    sendInvitesToContactsAt: string;
    timezone: string;
    category: EventCategory;
    isRecurring: bool;
    recurrencePattern: string;
    recurrenceEndDate: string;
    originalEventId: string;
    createdAt: string;
    updatedAt: string;
    contactsIds: string[];
    horsesIds: string[];
    eventContacts: EventContactSimple[];
    /**
     * Populated in EventsWatermelonMiddleware.attachRelations (batched horse rows).
     * Note: `horses[].contacts` is empty unless fetched via
     * EventsWatermelonMiddleware.readWithHorseRelations.
     */
    horses?: HorseSimple[];
    /** Populated in EventsWatermelonMiddleware.attachRelations (batched contact rows). */
    contacts?: ContactSimple[];
}

export type EventsSimpleRelations = {
    contactsIds: string[];
    horsesIds: string[];
    eventContacts: EventContactSimple[];
    location?: LocationSimple;
    horses?: HorseSimple[];
    contacts?: ContactSimple[];
};

// Strip down the Event model to a simplified version, avoiding
// Watermelon's internal types and methods leaking to the app's state.
export function simplifyEvent(
    event: EventModel,
    relations?: EventsSimpleRelations,
): EventSimple {
    return {
        id: event.id,
        userId: event.userId,
        organisationId: event.organisationId,
        locationId: event.locationId,
        title: event.title,
        address: event.address,
        startsTime: event.startsTime,
        endsTime: event.endsTime,
        notes: event.notes,
        userReminder: event.userReminder,
        sendRemindersToContacts: event.contactsReminder,
        sendRemindersToOwners: event.ownersReminder,
        sendInvitesToContacts: event.contactsInvite,
        sendInvitesToContactsAt: event.sendInvitesToContactsAt,
        timezone: event.timezone,
        category: event.category,
        isRecurring: event.isRecurring,
        recurrencePattern: event.recurrencePattern,
        recurrenceEndDate: event.recurrenceEndDate,
        originalEventId: event.originalEventId,
        createdAt: event.createdAt.toISOString(),
        updatedAt: event.updatedAt.toISOString(),
        ...(relations || null),
    };
}
